/**
 * Where a room's match runs (ADR-0136): the main server's region, and every region with a match host of its own. A
 * region's id and name are the game's; its address is the deploy's (the main's `HOSTS`, `GET /api/regions`).
 */
export const REGIONS = {
  eu: { name: 'EUROPE', short: 'EU', place: 'GERMANY' },
  us: { name: 'NORTH AMERICA', short: 'NA', place: 'DALLAS' },
} as const;

export type Region = keyof typeof REGIONS;

/** Every region, in the order the menus list them. */
export const REGION_IDS = Object.keys(REGIONS) as Region[];

export const isRegion = (s: unknown): s is Region => typeof s === 'string' && Object.hasOwn(REGIONS, s);

/**
 * The main server's region: where someone plays before their client measured anything, and where a room goes while its
 * own region has no host to run it.
 */
export const HOME_REGION: Region = 'eu';

/** Round trips to each region (ms) as a client measured them: untrusted, they only move where that client plays. */
export type Pings = Partial<Record<Region, number>>;

/**
 * Pings as the server keeps them (whole ms, `1` … `max`), and how much better another region must be before AUTO moves
 * a player there (`sticky`, ms: two regions about as near don't swap back and forth as the measures wobble).
 */
export const PING = { max: 9999, sticky: 20 } as const;

/**
 * Where someone plays and what they measured: their pick (null: AUTO), their pings, and AUTO's region as their client
 * keeps it (`best`: sticky, `bestRegion` with the one before; none said: the lowest round trip).
 */
export interface RegionChoice {
  pings: Pings;
  pick: Region | null;
  best?: Region;
}

/**
 * The region `pings` say plays best: the lowest round trip, but `current` while it is within `PING.sticky` ms of that.
 * Nothing measured: `current`, else `HOME_REGION`.
 */
export function bestRegion(pings: Pings, current?: Region | null): Region {
  let best: Region | null = null;
  for (const r of REGION_IDS) {
    const ms = pings[r];
    if (ms === undefined) continue;
    if (best === null || ms < (pings[best] as number)) best = r;
  }
  if (best === null) return current ?? HOME_REGION;
  const now = current ? pings[current] : undefined;
  return current && now !== undefined && now - (pings[best] as number) <= PING.sticky ? current : best;
}

/** Where someone plays on their own (ADR-0136): the region they picked, else AUTO's (their client's, else the lowest). */
export const ownRegion = (choice: RegionChoice | null | undefined): Region =>
  choice?.pick ?? choice?.best ?? bestRegion(choice?.pings ?? {});

/**
 * Where a party plays (ADR-0136), the leader first in `members`: every member's own region (`ownRegion`) the same, that
 * one; otherwise it is `cross`-region, and plays the leader's pick if they made one, else the region whose worst round
 * trip in the party is the lowest (a member who measured nothing there counts as never reaching it; nobody measured
 * anything that every member reaches: the leader's own).
 */
export function partyRegion(members: readonly (RegionChoice | null | undefined)[]): { region: Region; cross: boolean } {
  const own = members.map(ownRegion);
  const leader = own[0] ?? HOME_REGION;
  if (own.every((r) => r === leader)) return { region: leader, cross: false };
  const pick = members[0]?.pick;
  if (pick) return { region: pick, cross: true };
  let region = leader;
  let lowest = Number.POSITIVE_INFINITY;
  for (const r of REGION_IDS) {
    const worst = Math.max(...members.map((m) => m?.pings[r] ?? Number.POSITIVE_INFINITY));
    if (worst < lowest) {
      region = r;
      lowest = worst;
    }
  }
  return { region, cross: true };
}
